import React, { useState } from "react";
import classNames from "classnames";
import Image from "components/imageNew/image";
import { RoundIconButton } from "components/iconButton/iconButton";
import { ChevronIconWithDirection } from "components/icons/icons";
import { LinkButton } from "components/simpleButton/linkButton";
import type { LatestItemCMS } from "lib/types/homepage";

export type LatestCarouselItem = LatestItemCMS;

const DESKTOP_CAROUSEL_GAP = 16;
const DESKTOP_PREVIEW_WIDTH = 95;
const DESKTOP_PREVIEW_HOVER_WIDTH = 112;
const DESKTOP_PREVIEW_MAIN_HOVER_WIDTH = 87;

export type LatestCarouselProps = {
  items: LatestItemCMS[];
  className?: string;
  "data-testid"?: string;
};

/**
 * Custom carousel matching the Figma design.
 * Reference: https://www.figma.com/design/umAhixdLV0TO18YP3Noudb/Homepage-v2.0---Q1-2026?node-id=3499-22055
 */
export const LatestCarousel: React.FC<LatestCarouselProps> = ({
  items,
  className,
  "data-testid": dataTestId,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredPreviewIndex, setHoveredPreviewIndex] = useState<number | null>(
    null
  );
  const [isMainHovered, setIsMainHovered] = useState(false);

  const previewCount = Math.min(4, items.length - 1);

  if (items.length === 0) return null;

  const activeItem = items[activeIndex];

  const getPreviewItem = (previewPosition: number) => {
    const itemIndex = (activeIndex + previewPosition + 1) % items.length;
    return items[itemIndex];
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % items.length);
  };

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const handlePreviewClick = (previewPosition: number) => {
    const newIndex = (activeIndex + previewPosition + 1) % items.length;
    setActiveIndex(newIndex);
  };

  const previewWidths = Array.from(
    { length: previewCount },
    (_, previewPosition) => {
      if (hoveredPreviewIndex === previewPosition) {
        return DESKTOP_PREVIEW_HOVER_WIDTH;
      }

      if (isMainHovered) {
        return DESKTOP_PREVIEW_MAIN_HOVER_WIDTH;
      }

      return DESKTOP_PREVIEW_WIDTH;
    }
  );

  const previewStackWidth = previewWidths.reduce(
    (total, width) => total + width,
    0
  );
  const previewGapsWidth =
    previewCount > 0 ? previewCount * DESKTOP_CAROUSEL_GAP : 0;
  const mainCarouselWidth = `calc(100% - ${previewStackWidth + previewGapsWidth}px)`;

  return (
    <div
      className={classNames("flex flex-col gap-4", className)}
      data-testid={dataTestId || "latest-carousel"}
    >
      <div className="scrollbar-hide flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 md:hidden">
        {items.map((item, index) => (
          <div
            key={item.id}
            className="flex w-[calc(100vw-80px)] flex-shrink-0 snap-start flex-col gap-4"
          >
            <div className="overflow-hidden">
              {item.image && (
                <Image
                  src={item.image.url}
                  alt={item.image.alt || `Item ${index + 1}`}
                  width={390}
                  height={219}
                  blurDataURL={item.image.blurUpThumb}
                  className="aspect-video h-auto w-full object-cover"
                  sizes="(max-width: 768px) calc(100vw - 80px)"
                />
              )}
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                {item.title && (
                  <h3 className="font-recklessBold text-accent-26 text-black-rock">
                    {item.title}
                  </h3>
                )}
                {item.description && (
                  <p className="text-assist-14 text-black-rock">
                    {item.description}
                  </p>
                )}

                {item.button?.text && item.button?.url && (
                  <LinkButton
                    href={item.button.url}
                    size="lg"
                    variant="secondary"
                    accent="neutral"
                    className="w-fit whitespace-nowrap"
                  >
                    {item.button.text}
                  </LinkButton>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden w-full gap-4 md:flex">
        <div
          className="relative min-w-0 flex-none overflow-hidden transition-[width] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          data-testid="latest-carousel-main-panel"
          onMouseEnter={() => setIsMainHovered(true)}
          onMouseLeave={() => setIsMainHovered(false)}
          style={{
            height: "427px",
            width: mainCarouselWidth,
          }}
        >
          <div
            className="flex h-full will-change-transform"
            style={{
              transform: `translateX(-${activeIndex * (100 / items.length)}%)`,
              width: `${items.length * 100}%`,
              transition: "transform 0.9s cubic-bezier(0.23, 1, 0.32, 1)",
            }}
          >
            {items.map((item, index) => (
              <div
                key={item.id}
                className="relative h-full w-full flex-shrink-0 overflow-hidden"
                style={{ width: `${100 / items.length}%` }}
              >
                {item.image && (
                  <Image
                    src={item.image.url}
                    alt={item.image.alt || `Item ${index + 1}`}
                    width={808}
                    height={427}
                    blurDataURL={item.image.blurUpThumb}
                    className="absolute inset-y-0 left-1/2 h-full w-auto max-w-none -translate-x-1/2 object-cover"
                    sizes="(min-width: 1024px) 70vw, 100vw"
                    priority={index === activeIndex}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-shrink-0 gap-4" style={{ width: "auto" }}>
          {Array.from({ length: previewCount }).map((_, previewPosition) => {
            const previewItem = getPreviewItem(previewPosition);
            const previewWidth = previewWidths[previewPosition];

            return (
              <button
                key={`preview-${previewPosition}`}
                onClick={() => handlePreviewClick(previewPosition)}
                onMouseEnter={() => setHoveredPreviewIndex(previewPosition)}
                onMouseLeave={() => setHoveredPreviewIndex(null)}
                className="relative hidden h-[427px] flex-shrink-0 cursor-pointer overflow-hidden bg-gray-100 transition-[width] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] md:block"
                aria-label={`View item ${previewPosition + 1}`}
                type="button"
                style={{ width: `${previewWidth}px` }}
              >
                {previewItem.image && (
                  <Image
                    src={previewItem.image.url}
                    alt={
                      previewItem.image.alt || `Preview ${previewPosition + 1}`
                    }
                    width={112}
                    height={427}
                    blurDataURL={previewItem.image.blurUpThumb}
                    className="h-full w-full object-cover"
                    sizes="112px"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="hidden items-start justify-between gap-6 md:flex">
        <div className="flex w-full flex-col items-start justify-start gap-4 lg:flex-row lg:items-center lg:gap-6 mdDesktop:flex-row mdDesktop:items-center mdDesktop:gap-6">
          <div className="flex flex-col gap-2 lg:max-w-[602px] mdDesktop:max-w-[602px]">
            {activeItem.title && (
              <h3 className="font-recklessBold text-accent-26 text-black-rock">
                {activeItem.title}
              </h3>
            )}
            {activeItem.description && (
              <p className="text-assist-14 text-black-rock">
                {activeItem.description}
              </p>
            )}
          </div>

          {activeItem.button?.text && activeItem.button?.url && (
            <LinkButton
              href={activeItem.button.url}
              size="lg"
              variant="secondary"
              accent="neutral"
              className="w-fit flex-shrink-0 whitespace-nowrap"
            >
              {activeItem.button.text}
            </LinkButton>
          )}
        </div>

        <div className="flex flex-shrink-0 gap-2">
          <RoundIconButton
            onClick={handlePrev}
            disabled={items.length <= 1}
            variant="sifted-carousel"
            className="h-10 w-10"
            icon={
              <ChevronIconWithDirection
                direction="left"
                className="h-4 w-4 text-black-rock"
              />
            }
            aria-label="Previous item"
          />

          <RoundIconButton
            onClick={handleNext}
            disabled={items.length <= 1}
            variant="sifted-carousel"
            className="h-10 w-10"
            icon={
              <ChevronIconWithDirection
                direction="right"
                className="h-4 w-4 text-black-rock"
              />
            }
            aria-label="Next item"
          />
        </div>
      </div>
    </div>
  );
};

export default LatestCarousel;
